import { NETWORK_ERROR_CODE } from '../services/network-error';
import { codiceErrore } from './codice-errore';

/**
 * Il lettore delle tre forme d'errore vive in `codice-errore.ts`: la stessa
 * domanda se la fanno anche la dashboard e la lista dei collegati. Qui resta il
 * nome con cui l'arena lo chiama da sempre.
 */
export const arenaErrorCode = codiceErrore;

/**
 * Dal codice del server alla chiave i18n.
 *
 * Il server dell'arena risponde con due cose: un `code` stabile e una frase
 * inglese scritta per chi legge i log. La frase resta nei log; a schermo va la
 * traduzione, scelta sul codice. È lo stesso patto che `multiplayer-store.ts`
 * ha gia con `online.errors.*` (vedi `AUTH_REQUIRED` li dentro); nell'arena
 * mancava, e i messaggi finivano o in un bottone grigio muto o a schermo in
 * inglese crudo, comunque parlasse il resto della pagina.
 *
 * Codici diversi possono condividere una chiave quando per chi legge sono la
 * stessa situazione: `AUTH_REQUIRED` e `NOT_AUTHENTICATED` sono due punti del
 * codice del server, una sola cosa da dire.
 */
export const ARENA_ERROR_KEYS: Readonly<Record<string, string>> = {
  /* --- dai gestori socket e REST dell'arena (packages/server/src/arena) --- */
  AUTH_REQUIRED: 'arena.errors.authRequired',
  NOT_AUTHENTICATED: 'arena.errors.authRequired',
  GUEST_NOT_ALLOWED: 'arena.errors.guestNotAllowed',
  NOT_ALLOWED: 'arena.errors.notAllowed',
  ARENA_NOT_FOUND: 'arena.errors.arenaNotFound',
  USER_NOT_FOUND: 'arena.errors.userNotFound',
  INVALID_INPUT: 'arena.errors.invalidInput',
  RATE_LIMITED: 'arena.errors.rateLimited',
  /* Il rifiuto del rate limiter delle rotte REST, forma
     `{ error: 'RATE_LIMIT_EXCEEDED' }` invece di `{ code: 'RATE_LIMITED' }`.
     Oggi nessuna schermata chiama quelle rotte, quindi non arriva mai: sta qui
     perche il giorno che ci arrivera dice gia la stessa cosa dell'altro. */
  RATE_LIMIT_EXCEEDED: 'arena.errors.rateLimited',
  /* Lo SBARRAMENTO: il punteggio e' sotto il minimo che l'arena chiede.
     Lo stesso codice dei tornei (`RIFIUTO_SBARRAMENTO`, lato server), perche'
     dei codici non se ne inventano di nuovi — ma una chiave SUA, perche' la
     frase del torneo dice «questo torneo», che su un'arena e' la parola
     sbagliata. Prima di questa riga il rifiuto usciva come `JOIN_ERROR`, cioe'
     «non siamo riusciti a iscriverti, riprova», detto a chi era stato escluso
     di proposito. Il numero non entra nel messaggio: questa mappa non porta
     parametri, e lo sbarramento si legge sulla card dell'arena e nella sua
     scheda «Dettagli», prima di provare. */
  RATING_TOO_LOW: 'arena.errors.ratingTooLow',
  JOIN_ERROR: 'arena.errors.joinFailed',
  /*
   * I tre rifiuti dell'iscrizione, che prima erano un `JOIN_ERROR` solo.
   *
   * `joinArena` rispondeva `null` per tre cause diverse e il gestore le
   * appiattiva tutte su «non siamo riusciti a iscriverti, riprova» — una frase
   * che per due di loro e' falsa e per la terza e' dannosa: a chi trova
   * l'arena PIENA (migration 104) dice di riprovare, e riprovare non servira'
   * mai. Adesso ognuna porta il suo codice.
   */
  ARENA_PIENA: 'arena.errors.arenaPiena',
  ARENA_CHIUSA: 'arena.errors.arenaChiusa',
  GIA_ISCRITTO: 'arena.errors.giaIscritto',
  /*
   * Il tempo dell'arena e' finito, e si aspettano solo le ultime partite.
   *
   * Non e' `ARENA_CHIUSA` («non accetta piu' iscrizioni»): quella frase parla
   * d'iscrizioni, e questo codice lo legge anche chi e' iscritto da un'ora e ha
   * appena premuto «Continua» dopo la sua ultima partita. Non e' nemmeno
   * `TOO_LATE`, che parla della finestra del boost. Lo mandano due porte: la
   * prima iscrizione e la ripresa della ricerca.
   */
  ARENA_SCADUTA: 'arena.errors.arenaScaduta',
  /* Si e' gia' in un'arena IN CORSO: guardarne un'altra si puo', iscriversi no. */
  GIA_IN_UN_ARENA: 'arena.errors.giaInUnArena',
  NOT_IN_ARENA: 'arena.errors.notInArena',
  NOT_IN_GAME: 'arena.errors.notInGame',
  GAME_NOT_FOUND: 'arena.errors.gameNotFound',
  TOO_LATE: 'arena.errors.tooLate',
  /*
   * In quest'arena il boost e' spento (migration 118).
   *
   * Un codice NUOVO, e qui la regola «di codici non se ne inventano» va letta
   * per intero: non se ne inventano quando ce n'e' gia' uno che dice la stessa
   * cosa. Nessuno dei tre candidati la dice. `NOT_ALLOWED` porta «questo
   * account non puo' iscriversi a un'arena», che parla d'iscrizione e qui
   * sarebbe falsa; `BOOST_FAILED` porta «riprova», detto a chi chiede una cosa
   * che in quest'arena non riuscira' mai; `TOO_LATE` parla di una finestra che
   * qui non si e' chiusa — non si e' mai aperta. E' lo stesso ragionamento che
   * ha diviso il vecchio `JOIN_ERROR` nei tre rifiuti dell'iscrizione qui
   * sopra: la frase sbagliata manda la persona a rifare il gesto.
   */
  BOOST_DISABLED: 'arena.errors.boostDisabled',
  ALREADY_BOOSTED: 'arena.errors.alreadyBoosted',
  NOT_BOOSTED: 'arena.errors.notBoosted',
  BOOST_FAILED: 'arena.errors.boostFailed',
  UNBOOST_FAILED: 'arena.errors.unboostFailed',
  INTERNAL_ERROR: 'arena.errors.serverError',

  /* --- i rifiuti di CHI ORGANIZZA (POST /:id/cancel, migration 100) --- */
  /* Due rifiuti legittimi, non due guasti: l'arena non e' tua, oppure e' gia'
     partita mentre stavi per annullarla. Senza queste due righe finivano
     entrambi su «qualcosa e' andato storto», che manda a cercare la causa
     sbagliata. Dicono «arena» e non «torneo»: sono le stesse frasi dei tornei
     con la parola giusta, ed e' tutta la ragione per cui non si riusa quella
     mappa. */
  NOT_ORGANIZER: 'arena.errors.notOrganizer',
  ALREADY_STARTED: 'arena.errors.alreadyStarted',

  /* --- dal funnel generico (packages/server/src/middleware/error-handler.ts) --- */
  /* Le quattro GET dell'arena fanno `next(error)` e `arena.service.ts` lancia
     `Error` nudi, non `AppError`: ogni guasto vero — database giu, query rotta
     — esce di li come `{ error: 'INTERNAL_SERVER_ERROR' }`. E il guasto piu
     probabile di un caricamento arena, e senza questa riga finiva su
     «errore sconosciuto». Non e lo stesso codice di `INTERNAL_ERROR`, che e
     quello dei gestori socket: due strade diverse, la stessa frase. */
  INTERNAL_SERVER_ERROR: 'arena.errors.serverError',
  /* `notFoundHandler` per una rotta inesistente, e `NotFoundError`
     (utils/errors.ts) per una risorsa mancante. In una schermata d'arena
     entrambe vogliono dire una cosa sola: quell'arena non c'e. */
  NOT_FOUND: 'arena.errors.arenaNotFound',

  /* --- nati sul client: la richiesta non e mai arrivata al server --- */
  /* `ArenaSocketError` quando il socket non e connesso (services/socket.ts). */
  NOT_CONNECTED: 'arena.errors.offline',
  /* `fetch()` che fallisce a livello di rete (services/api.ts).
     ⚠️ Attenzione a come si arriva qui: questo codice NON sta in `.code`.
     `api.ts` lo lancia come `new Error(NETWORK_ERROR_CODE)` — un Error nudo,
     dove il codice sta in `.message`. `.code` esiste solo sugli errori HTTP
     costruiti da `buildApiError`. Quindi `arenaErrorKey((err as ApiRequestError).code)`
     su una rete caduta darebbe `unknown` invece di «sembri offline»: chi
     chiama deve guardare anche `err.message === NETWORK_ERROR_CODE`, come fa
     gia `AuthModal.tsx`. Un normalizzatore delle tre forme
     (`ArenaSocketError.code`, `ApiRequestError.code`, `Error.message`) va
     scritto dove i codici si consumano davvero, cioe nello store. */
  [NETWORK_ERROR_CODE]: 'arena.errors.offline',
  /* `ArenaSocketError` quando l'ack non torna entro il tempo massimo. */
  TIMEOUT: 'arena.errors.timeout',
};

/** La chiave di ripiego: si dice qualcosa, mai niente. */
export const ARENA_UNKNOWN_ERROR_KEY = 'arena.errors.unknown';

/**
 * La chiave i18n per un codice del server. Restituisce sempre una chiave: un
 * codice nuovo, aggiunto lato server e non ancora tradotto, da comunque una
 * frase sensata invece di una casella vuota o di una sigla in maiuscolo.
 */
export function arenaErrorKey(code: string | undefined | null): string {
  if (!code) return ARENA_UNKNOWN_ERROR_KEY;
  return ARENA_ERROR_KEYS[code] ?? ARENA_UNKNOWN_ERROR_KEY;
}

/** La chiave i18n per un errore, qualunque forma abbia. Vedi `arenaErrorCode`. */
export function arenaErrorKeyFor(err: unknown): string {
  return arenaErrorKey(arenaErrorCode(err));
}
